import { useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant, Modal } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { loadPropertyOptions } from "api/landlord/asyncUnitProperties";
import { getErrorMessage } from "utils/error";
import { MhvillageExternalMapping } from "api/mhvillage";
import {
  useCreateExternalMapping,
  useUpdateExternalMapping
} from "../api/hooks";

interface MappingModalProps {
  opened: boolean;
  mode: "create" | "edit";
  mapping: MhvillageExternalMapping | null;
  communityLabel: string;
  onClose: () => void;
}

export const MappingModal = ({
  opened,
  mode,
  mapping,
  communityLabel,
  onClose
}: MappingModalProps) => {
  const [externalId, setExternalId] = useState(mapping?.external_id ?? "");
  const [externalIdError, setExternalIdError] = useState("");
  const [propertyId, setPropertyId] = useState<number | null>(
    mapping?.property_id ?? null
  );
  const [communityLabelValue, setCommunityLabelValue] = useState(
    mapping?.property_name ?? undefined
  );

  const { mutate: createMapping, isPending: isCreating } =
    useCreateExternalMapping();
  const { mutate: updateMapping, isPending: isUpdating } =
    useUpdateExternalMapping();

  const isPending = isCreating || isUpdating;
  const isEdit = mode === "edit";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!externalId.trim()) {
      setExternalIdError("External ID is required");
      return;
    }
    setExternalIdError("");

    const onSuccess = () => {
      toast.success(isEdit ? "Mapping updated" : "Mapping added");
      onClose();
    };

    const onError = (error: unknown) => {
      toast.error(getErrorMessage(error));
    };

    if (isEdit && mapping)
      updateMapping(
        {
          id: mapping.id,
          payload: { external_id: externalId.trim(), property_id: propertyId }
        },
        { onSuccess, onError }
      );
    else
      createMapping(
        { external_id: externalId.trim(), property_id: propertyId },
        { onSuccess, onError }
      );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[480px] w-full"
    >
      <form onSubmit={handleSubmit} className="relative">
        <div className="px-6 pt-6 pb-2 space-y-5">
          <div>
            <label
              htmlFor="mhvillage-external-id"
              className="block text-sm font-medium text-content-500 mb-1.5"
            >
              External ID
            </label>
            <BaseInput
              id="mhvillage-external-id"
              value={externalId}
              onChange={(value) => {
                setExternalId(value);
                if (externalIdError) setExternalIdError("");
              }}
              placeholder="Enter External ID"
              className="w-full"
              error={externalIdError || undefined}
            />
          </div>

          <div>
            <AsyncSelect
              id="mhvillage-community"
              label={communityLabel}
              hideOptional
              isClearable
              searchable
              menuPosition="fixed"
              placeholder={`Select ${communityLabel.toLowerCase()}`}
              value={propertyId ?? ""}
              options={
                propertyId && communityLabelValue
                  ? [{ value: propertyId, label: communityLabelValue }]
                  : []
              }
              // @ts-expect-error -- loadPropertyOptions is loosely typed like our other async selects
              loadOptions={loadPropertyOptions}
              additional={{ page: 1 }}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  const nextValue = e.target.value;
                  setPropertyId(nextValue ? Number(nextValue) : null);
                  setCommunityLabelValue(e.label);
                }
              }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.BASE}
            onClick={onClose}
            type="button"
          >
            Cancel
          </Button>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            loading={isPending}
            type="submit"
          >
            {isEdit ? "Edit" : "Add"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
